/**
 * ProjectIntegrationsPanel
 *
 * Shows integration bindings for a project and lets admins attach, configure,
 * and detach workspace integrations. Only the ADO project mapping
 * (provider_project_id) is configured here; repos/boards/pipelines have
 * their own dedicated pages.
 *
 * Rendered from ProjectPage when the "Integrations" menu item is active.
 */
import type {
  IntegrationCredential,
  ProjectIntegration,
  ProjectIntegrationStatus,
} from '@protoss/shared';
import { Dialog, MultiSelect, Select, SelectItem } from '@protoss/ui';
import * as React from 'react';
import { useDirtyState } from '../../hooks/useDirtyState';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { apiFetch } from '../../lib/apiClient';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';
import { PageErrorState } from '../shared/PageErrorState';

// ── Schema field type ─────────────────────────────────────────────────────────

interface SchemaField {
  label: string;
  type?: string;
  description?: string;
  example?: string;
  required?: boolean;
  /** If set, render a dropdown populated by this resource discovery type. */
  resource_type?: string;
  /** If set, use the value of this sibling field as the parentId when fetching resources. */
  parent_field?: string;
}

function getSchemaFields(schema: Record<string, unknown>): [string, SchemaField][] {
  return Object.entries(schema).map(([key, raw]) => {
    const field = (raw && typeof raw === 'object' ? raw : {}) as SchemaField;
    return [key, { ...field, label: field.label ?? key }];
  });
}

// ── Status badge ──────────────────────────────────────────────────────────────

function IntegrationStatusBadge({ status }: { status: ProjectIntegrationStatus }) {
  const config: Record<ProjectIntegrationStatus, { label: string; cls: string }> = {
    configured: { label: 'Configured', cls: 'pi-badge pi-badge--configured' },
    attached: { label: 'Needs setup', cls: 'pi-badge pi-badge--attached' },
    error: { label: 'Error', cls: 'pi-badge pi-badge--error' },
    disabled: { label: 'Disabled', cls: 'pi-badge pi-badge--disabled' },
  };
  const { label, cls } = config[status];
  return <span className={cls}>{label}</span>;
}

// ── Attach modal ──────────────────────────────────────────────────────────────

interface AttachModalProps {
  projectUuid: string;
  onAttached: () => void;
  onClose: () => void;
}

function AttachModal({ projectUuid, onAttached, onClose }: AttachModalProps) {
  const [workspaceIntegrations, setWorkspaceIntegrations] = React.useState<IntegrationCredential[]>(
    []
  );
  const [selectedUuid, setSelectedUuid] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    apiFetch<{ integrations: IntegrationCredential[] }>('/api/integration')
      .then((data) => setWorkspaceIntegrations(data.integrations))
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  async function handleAttach() {
    if (!selectedUuid) return;
    try {
      setSubmitting(true);
      setError(null);
      await apiFetch(`/api/project/${projectUuid}/integration`, {
        method: 'POST',
        body: JSON.stringify({ integrationCredentialUuid: selectedUuid }),
      });
      onAttached();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content" aria-describedby={undefined}>
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">Attach integration</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>

          {loading && <p className="pi-modal__loading">Loading workspace integrations…</p>}

          {!loading && workspaceIntegrations.length === 0 && (
            <p className="pi-modal__empty">
              No workspace integrations have been configured yet. An admin can add them in the
              Administration → Integrations section.
            </p>
          )}

          {!loading && workspaceIntegrations.length > 0 && (
            <div className="pi-modal__body">
              <label className="pi-form-label">Workspace integration</label>
              <Select
                value={selectedUuid}
                onValueChange={setSelectedUuid}
                placeholder="Select a workspace integration…"
              >
                {workspaceIntegrations.map((wi) => (
                  <SelectItem key={wi.uuid} value={wi.uuid}>
                    {wi.displayName}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}

          {error && <p className="pi-modal__error">{error}</p>}

          <div className="pi-modal__actions">
            <button className="pi-btn pi-btn--ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              className="pi-btn pi-btn--primary"
              onClick={() => void handleAttach()}
              disabled={!selectedUuid || submitting}
            >
              {submitting ? 'Attaching…' : 'Attach'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Databricks config form ────────────────────────────────────────────────────

function DatabricksConfigForm({
  projectUuid,
  binding,
  canManage,
  onUpdated,
  onDetach,
}: ConfigFormProps) {
  // Parse persisted values from binding.config
  const initialCatalogs = React.useMemo(() => {
    const raw = binding.config.find((c) => c.key === 'catalog_name')?.value ?? '';
    return raw
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }, [binding.config]);

  const initialSchemas = React.useMemo(() => {
    const raw = binding.config.find((c) => c.key === 'schema_name')?.value ?? '';
    return raw
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }, [binding.config]);

  const initialTables = React.useMemo(() => {
    const raw = binding.config.find((c) => c.key === 'allowed_tables')?.value ?? '';
    return raw
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }, [binding.config]);

  const [enabled, setEnabled] = React.useState(binding.enabled);

  // Selection state
  const [selectedCatalogs, setSelectedCatalogs] = React.useState<string[]>(initialCatalogs);
  const [selectedSchemas, setSelectedSchemas] = React.useState<string[]>(initialSchemas);
  const [selectedTables, setSelectedTables] = React.useState<string[]>(initialTables);

  // Available items from API
  const [availableCatalogs, setAvailableCatalogs] = React.useState<string[]>([]);
  const [availableSchemas, setAvailableSchemas] = React.useState<
    { id: string; name: string; catalog: string }[]
  >([]);
  const [availableTables, setAvailableTables] = React.useState<string[]>([]);

  // Loading states - start catalogsLoading as true; the mount effect fires
  // immediately and we never want to show an empty list before data arrives.
  const [catalogsLoading, setCatalogsLoading] = React.useState(true);
  const [schemasLoading, setSchemasLoading] = React.useState(false);
  const [tablesLoading, setTablesLoading] = React.useState(false);

  const [saving, setSaving] = React.useState(false);
  const [detaching, setDetaching] = React.useState(false);
  const [detachConfirmOpen, setDetachConfirmOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    enabled: boolean;
    selectedCatalogs: string[];
    selectedSchemas: string[];
    selectedTables: string[];
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      enabled: binding.enabled,
      selectedCatalogs: [...initialCatalogs].sort(),
      selectedSchemas: [...initialSchemas].sort(),
      selectedTables: [...initialTables].sort(),
    }),
    [binding.enabled, initialCatalogs, initialSchemas, initialTables]
  );

  const currentPayload = React.useMemo(
    () => ({
      enabled,
      selectedCatalogs: [...selectedCatalogs].sort(),
      selectedSchemas: [...selectedSchemas].sort(),
      selectedTables: [...selectedTables].sort(),
    }),
    [enabled, selectedCatalogs, selectedSchemas, selectedTables]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent(currentPayload);
  }, [currentPayload, updateCurrent]);

  useUnsavedChanges(!saving && isDirty, 'rr:integration-databricks');

  async function loadSchemas(catalogs: string[]) {
    if (catalogs.length === 0) {
      setAvailableSchemas([]);
      return;
    }
    setSchemasLoading(true);
    try {
      const qs = new URLSearchParams({ catalogs: catalogs.join(',') });
      const data = await apiFetch<{ schemas: { id: string; name: string; catalog: string }[] }>(
        `/api/project/${projectUuid}/data-catalog/meta/schemas?${qs}`
      );
      setAvailableSchemas(data.schemas);
    } catch {
      setAvailableSchemas([]);
    } finally {
      setSchemasLoading(false);
    }
  }

  async function loadTables(schemaIds: string[]) {
    if (schemaIds.length === 0) {
      setAvailableTables([]);
      return;
    }
    setTablesLoading(true);
    try {
      const qs = new URLSearchParams({ schemas: schemaIds.join(',') });
      const data = await apiFetch<{ tables: string[] }>(
        `/api/project/${projectUuid}/data-catalog/meta/tables?${qs}`
      );
      setAvailableTables(data.tables);
    } catch {
      setAvailableTables([]);
    } finally {
      setTablesLoading(false);
    }
  }

  // On mount: load catalogs, then schemas/tables for any pre-selected values.
  // Uses a per-invocation `cancelled` closure variable instead of a shared ref
  // so React Strict Mode's mount→cleanup→remount cycle properly cancels the
  // first invocation's callbacks before the second invocation starts.
  React.useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setCatalogsLoading(true);
      try {
        const data = await apiFetch<{ catalogs: string[] }>(
          `/api/project/${projectUuid}/data-catalog/meta/catalogs`
        );
        if (cancelled) return;
        setAvailableCatalogs(data.catalogs);
      } catch {
        // ignore; user will see empty list
      } finally {
        if (!cancelled) setCatalogsLoading(false);
      }

      if (cancelled || initialCatalogs.length === 0) return;

      setSchemasLoading(true);
      try {
        const sq = new URLSearchParams({ catalogs: initialCatalogs.join(',') });
        const schemasData = await apiFetch<{
          schemas: { id: string; name: string; catalog: string }[];
        }>(`/api/project/${projectUuid}/data-catalog/meta/schemas?${sq}`);
        if (cancelled) return;
        setAvailableSchemas(schemasData.schemas);
      } catch {
        if (!cancelled) setAvailableSchemas([]);
      } finally {
        if (!cancelled) setSchemasLoading(false);
      }

      if (cancelled || initialSchemas.length === 0) return;

      setTablesLoading(true);
      try {
        const tq = new URLSearchParams({ schemas: initialSchemas.join(',') });
        const tablesData = await apiFetch<{ tables: string[] }>(
          `/api/project/${projectUuid}/data-catalog/meta/tables?${tq}`
        );
        if (cancelled) return;
        setAvailableTables(tablesData.tables);
      } catch {
        if (!cancelled) setAvailableTables([]);
      } finally {
        if (!cancelled) setTablesLoading(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCatalogChange(next: string[]) {
    setSelectedCatalogs(next);
    // Reset downstream selections
    setSelectedSchemas([]);
    setSelectedTables([]);
    void loadSchemas(next);
  }

  function handleSchemaChange(next: string[]) {
    setSelectedSchemas(next);
    // Reset tables
    setSelectedTables([]);
    void loadTables(next);
  }

  function handleTableChange(next: string[]) {
    setSelectedTables(next);
  }

  async function handleSave() {
    if (selectedCatalogs.length === 0) {
      setError('Select at least one catalog.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await apiFetch(`/api/project/${projectUuid}/data-catalog/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          catalog_name: selectedCatalogs.join(','),
          schema_name: selectedSchemas.length > 0 ? selectedSchemas.join(',') : null,
          allowed_tables: selectedTables.length > 0 ? selectedTables.join(',') : null,
          enabled,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDetach() {
    try {
      setDetaching(true);
      setError(null);
      await apiFetch(`/api/project/${projectUuid}/integration/${binding.uuid}`, {
        method: 'DELETE',
      });
      onDetach();
    } catch (err: unknown) {
      setError((err as Error).message);
      setDetachConfirmOpen(false);
    } finally {
      setDetaching(false);
    }
  }

  return (
    <div className="pi-config-form">
      <div className="pi-config-form__fields">
        {/* Catalog */}
        <div className="pi-form-field">
          <MultiSelect
            label="Catalog *"
            options={availableCatalogs.map((c) => ({ value: c, label: c }))}
            selectedValues={selectedCatalogs}
            originalValues={initialCatalogs}
            onSelectionChange={handleCatalogChange}
            loading={catalogsLoading}
            disabled={!canManage}
            placeholder="Select catalogs…"
          />
        </div>

        {/* Schema (optional, loaded after catalog selection) */}
        <div className="pi-form-field">
          <MultiSelect
            label="Schema"
            options={availableSchemas.map((s) => ({
              value: s.id,
              label: s.name,
              sublabel: s.catalog,
            }))}
            selectedValues={selectedSchemas}
            originalValues={initialSchemas}
            onSelectionChange={handleSchemaChange}
            loading={schemasLoading}
            disabled={!canManage || selectedCatalogs.length === 0}
            placeholder={
              selectedCatalogs.length === 0
                ? 'Select a catalog first…'
                : 'Select schemas… (optional)'
            }
          />
        </div>

        {/* Tables (optional, loaded after schema selection) */}
        <div className="pi-form-field">
          <MultiSelect
            label="Allowed Tables"
            options={availableTables.map((t) => ({ value: t, label: t }))}
            selectedValues={selectedTables}
            originalValues={initialTables}
            onSelectionChange={handleTableChange}
            loading={tablesLoading}
            disabled={!canManage || selectedSchemas.length === 0}
            placeholder={
              selectedSchemas.length === 0 ? 'Select a schema first…' : 'Select tables… (optional)'
            }
          />
        </div>
      </div>

      {canManage && (
        <div className="pi-form-field">
          <label className="pi-form-toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Enabled</span>
          </label>
        </div>
      )}

      {error && <p className="pi-form-error">{error}</p>}

      {canManage && (
        <div className="pi-config-form__actions">
          <button
            className="pi-btn pi-btn--primary"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saved ? 'Saved!' : saving ? 'Saving…' : 'Save'}
          </button>
          <button
            className="pi-btn pi-btn--danger"
            onClick={() => setDetachConfirmOpen(true)}
            disabled={detaching}
          >
            {detaching ? 'Detaching…' : 'Detach'}
          </button>
          <ConfirmDeleteDialog
            open={detachConfirmOpen}
            onOpenChange={setDetachConfirmOpen}
            title="Detach integration?"
            confirmKeyword={binding.workspaceIntegration.displayName}
            description="This integration will be detached from the project."
            confirmLabel="Detach"
            pendingLabel="Detaching…"
            isPending={detaching}
            onConfirm={handleDetach}
          />
        </div>
      )}
    </div>
  );
}

// ── Config form ───────────────────────────────────────────────────────────────

interface ConfigFormProps {
  projectUuid: string;
  binding: ProjectIntegration;
  canManage: boolean;
  onUpdated: () => void;
  onDetach: () => void;
}

function ConfigForm({ projectUuid, binding, canManage, onUpdated, onDetach }: ConfigFormProps) {
  const schema = binding.workspaceIntegration.definition.projectConfigSchema;
  // Required fields sorted to the top.
  const fields: [string, SchemaField][] = schema
    ? getSchemaFields(schema).sort(([, a], [, b]) => {
        if (a.required && !b.required) return -1;
        if (!a.required && b.required) return 1;
        return 0;
      })
    : [];

  const initialValues = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const { key, value } of binding.config) map[key] = value;
    return map;
  }, [binding.config]);

  const [values, setValues] = React.useState<Record<string, string>>(initialValues);
  const [enabled, setEnabled] = React.useState(binding.enabled);
  const [saving, setSaving] = React.useState(false);
  const [detaching, setDetaching] = React.useState(false);
  const [detachConfirmOpen, setDetachConfirmOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    enabled: boolean;
    values: Record<string, string>;
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      enabled: binding.enabled,
      values: initialValues,
    }),
    [binding.enabled, initialValues]
  );

  const currentPayload = React.useMemo(
    () => ({
      enabled,
      values,
    }),
    [enabled, values]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent(currentPayload);
  }, [currentPayload, updateCurrent]);

  useUnsavedChanges(!saving && isDirty, 'rr:integration-generic');

  type FieldOptions = {
    items: Array<{ id: string; name: string }>;
    loading: boolean;
    error: string | null;
  };
  const [resourceOptions, setResourceOptions] = React.useState<Record<string, FieldOptions>>({});

  async function loadFieldResources(fieldKey: string, resourceType: string, parentId?: string) {
    setResourceOptions((prev) => ({
      ...prev,
      [fieldKey]: { items: [], loading: true, error: null },
    }));
    try {
      const qs = new URLSearchParams({ type: resourceType });
      if (parentId) qs.set('parentId', parentId);
      const data = await apiFetch<{ resources: Array<{ id: string; name: string }> }>(
        `/api/project/${projectUuid}/integration/${binding.uuid}/resources?${qs.toString()}`
      );
      setResourceOptions((prev) => ({
        ...prev,
        [fieldKey]: { items: data.resources, loading: false, error: null },
      }));
    } catch (err) {
      setResourceOptions((prev) => ({
        ...prev,
        [fieldKey]: { items: [], loading: false, error: (err as Error).message },
      }));
    }
  }

  function handleFieldChange(fieldKey: string, newValue: string) {
    // Also capture the human-readable name for resource fields so it can be persisted
    const itemName = resourceOptions[fieldKey]?.items.find((i) => i.id === newValue)?.name;
    setValues((v) => ({
      ...v,
      [fieldKey]: newValue,
      [`${fieldKey}_name`]: itemName ?? '',
    }));
    // Cascade: when a parent field changes, reset and reload its children.
    const children = fields.filter(([, f]) => f.parent_field === fieldKey && f.resource_type);
    for (const [childKey, childField] of children) {
      setValues((v) => ({ ...v, [childKey]: '', [`${childKey}_name`]: '' }));
      if (newValue) {
        void loadFieldResources(childKey, childField.resource_type!, newValue);
      } else {
        setResourceOptions((prev) => ({
          ...prev,
          [childKey]: { items: [], loading: false, error: null },
        }));
      }
    }
  }

  // Load root resource fields on mount; pre-load children if parent value already set.
  React.useEffect(() => {
    for (const [key, field] of fields) {
      if (!field.resource_type) continue;
      if (!field.parent_field) {
        void loadFieldResources(key, field.resource_type);
      } else {
        const parentValue = initialValues[field.parent_field] ?? '';
        if (parentValue) void loadFieldResources(key, field.resource_type, parentValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setValues((prev) => {
      let next = prev;

      for (const [key, field] of fields) {
        if (!field.resource_type) continue;

        const selectedValue = prev[key];
        if (!selectedValue) continue;

        const selectedItem = resourceOptions[key]?.items.find((item) => item.id === selectedValue);
        if (!selectedItem) continue;

        const nameKey = `${key}_name`;
        if (prev[nameKey] === selectedItem.name) continue;

        if (next === prev) next = { ...prev };
        next[nameKey] = selectedItem.name;
      }

      return next;
    });
  }, [fields, resourceOptions]);

  async function handleSave() {
    const missingLabels = fields
      .filter(([key, field]) => field.required && !values[key]?.trim())
      .map(([, field]) => field.label);
    if (missingLabels.length > 0) {
      setError(`Required: ${missingLabels.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await apiFetch(`/api/project/${projectUuid}/integration/${binding.uuid}/project-config`, {
        method: 'PUT',
        body: JSON.stringify({
          provider_project_id: values.provider_project_id ?? '',
          provider_project_name:
            values.provider_project_id_name ?? values.provider_project_name ?? '',
          enabled,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDetach() {
    try {
      setDetaching(true);
      setError(null);
      await apiFetch(`/api/project/${projectUuid}/integration/${binding.uuid}`, {
        method: 'DELETE',
      });
      onDetach();
    } catch (err: unknown) {
      setError((err as Error).message);
      setDetachConfirmOpen(false);
    } finally {
      setDetaching(false);
    }
  }

  return (
    <div className="pi-config-form">
      {fields.length > 0 && (
        <div className="pi-config-form__fields">
          {fields.map(([key, field]) => (
            <div key={key} className="pi-form-field">
              <label className="pi-form-label" htmlFor={`pi-field-${key}`}>
                {field.label}
                {field.required && (
                  <span className="pi-form-required" aria-hidden="true">
                    {' '}
                    *
                  </span>
                )}
              </label>
              {field.resource_type ? (
                <Select
                  value={values[key] ?? undefined}
                  onValueChange={(val) => handleFieldChange(key, val)}
                  placeholder={
                    resourceOptions[key]?.loading
                      ? 'Loading…'
                      : `Select ${field.label.toLowerCase()}…`
                  }
                  disabled={!canManage || resourceOptions[key]?.loading}
                >
                  {/* Show the saved value as a fallback if not in the loaded list yet */}
                  {values[key] &&
                    !resourceOptions[key]?.items.find((i) => i.id === values[key]) && (
                      <SelectItem value={values[key]}>
                        {values[`${key}_name`] || values[key]}
                      </SelectItem>
                    )}
                  {resourceOptions[key]?.items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </Select>
              ) : (
                <input
                  id={`pi-field-${key}`}
                  className="pi-form-input"
                  type="text"
                  value={values[key] ?? ''}
                  placeholder={field.example ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  disabled={!canManage}
                />
              )}
              {resourceOptions[key]?.error && (
                <span className="pi-form-hint pi-form-hint--error">
                  {resourceOptions[key].error}
                </span>
              )}
              {field.description && !resourceOptions[key]?.error && (
                <span className="pi-form-hint">{field.description}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="pi-form-field">
          <label className="pi-form-toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Enabled</span>
          </label>
        </div>
      )}

      {error && <p className="pi-form-error">{error}</p>}

      {canManage && (
        <div className="pi-config-form__actions">
          <button
            className="pi-btn pi-btn--primary"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saved ? 'Saved!' : saving ? 'Saving…' : 'Save'}
          </button>
          <button
            className="pi-btn pi-btn--danger"
            onClick={() => setDetachConfirmOpen(true)}
            disabled={detaching}
          >
            {detaching ? 'Detaching…' : 'Detach'}
          </button>
          <ConfirmDeleteDialog
            open={detachConfirmOpen}
            onOpenChange={setDetachConfirmOpen}
            title="Detach integration?"
            confirmKeyword={binding.workspaceIntegration.displayName}
            description="This integration will be detached from the project."
            confirmLabel="Detach"
            pendingLabel="Detaching…"
            isPending={detaching}
            onConfirm={handleDetach}
          />
        </div>
      )}
    </div>
  );
}

// ── Binding card ──────────────────────────────────────────────────────────────

interface BindingCardProps {
  projectUuid: string;
  binding: ProjectIntegration;
  canManage: boolean;
  onUpdated: () => void;
  onDetached: () => void;
}

function BindingCard({ projectUuid, binding, canManage, onUpdated, onDetached }: BindingCardProps) {
  const [expanded, setExpanded] = React.useState(false);
  const { definition } = binding.workspaceIntegration;

  return (
    <div className="pi-card">
      <div
        className="pi-card__header"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v);
        }}
      >
        <div className="pi-card__identity">
          <span className="pi-card__provider-name">{definition.name}</span>
          <span className="pi-card__display-name">{binding.workspaceIntegration.displayName}</span>
        </div>
        <div className="pi-card__meta">
          <IntegrationStatusBadge status={binding.status} />
          <span className="pi-card__chevron" aria-hidden="true">
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="pi-card__body">
          {definition.slug === 'databricks' ? (
            <DatabricksConfigForm
              projectUuid={projectUuid}
              binding={binding}
              canManage={canManage}
              onUpdated={onUpdated}
              onDetach={onDetached}
            />
          ) : (
            <ConfigForm
              projectUuid={projectUuid}
              binding={binding}
              canManage={canManage}
              onUpdated={onUpdated}
              onDetach={onDetached}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

interface ProjectIntegrationsPanelProps {
  projectUuid: string;
  canManage: boolean;
}

export function ProjectIntegrationsPanel({
  projectUuid,
  canManage,
}: ProjectIntegrationsPanelProps) {
  const [bindings, setBindings] = React.useState<ProjectIntegration[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showAttachModal, setShowAttachModal] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch<{ integrations: ProjectIntegration[] }>(`/api/project/${projectUuid}/integration`)
      .then((data) => setBindings(data.integrations))
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [projectUuid]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="pi-panel">
        <p className="pi-panel__loading">Loading integrations…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pi-panel">
        <PageErrorState message={`Failed to load integrations: ${error}`} onRetry={() => load()} />
      </div>
    );
  }

  return (
    <div className="pi-panel">
      <div className="pi-panel__header">
        <h2 className="pi-panel__title">Integrations</h2>
        {canManage && (
          <button className="pi-btn pi-btn--primary" onClick={() => setShowAttachModal(true)}>
            Attach integration
          </button>
        )}
      </div>

      {bindings.length === 0 ? (
        <div className="pi-panel__empty">
          <p>No integrations attached to this project yet.</p>
          {canManage && (
            <p>
              Use <strong>Attach integration</strong> to connect a workspace integration to this
              project.
            </p>
          )}
        </div>
      ) : (
        <div className="pi-panel__list">
          {bindings.map((b) => (
            <BindingCard
              key={b.uuid}
              projectUuid={projectUuid}
              binding={b}
              canManage={canManage}
              onUpdated={load}
              onDetached={load}
            />
          ))}
        </div>
      )}

      {showAttachModal && (
        <AttachModal
          projectUuid={projectUuid}
          onAttached={() => {
            setShowAttachModal(false);
            load();
          }}
          onClose={() => setShowAttachModal(false)}
        />
      )}
    </div>
  );
}
